import { useState } from 'react';
import axios from 'axios';
import EmployeeLayout from '../../components/layout/EmployeeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Settings, Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeeSettingsPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('New passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed. A confirmation email has been sent.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to change password';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <EmployeeLayout>
      <div className="p-4 sm:p-6 space-y-4 max-w-xl mx-auto">
        <div className="border-b border-border pb-4 flex items-center gap-3">
          <Settings className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground">SETTINGS</h1>
            <p className="text-xs text-muted-foreground mt-1">Manage your account security.</p>
          </div>
        </div>

        <Card className="rounded-sm border-border">
          <CardHeader>
            <CardTitle className="text-base font-barlow tracking-widest flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" /> CHANGE PASSWORD
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Current Password</Label>
                <Input type={show ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required data-testid="current-password-input" className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">New Password</Label>
                <div className="relative">
                  <Input type={show ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="new-password-input" className="rounded-sm pr-10" />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle password visibility">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground">At least 8 characters.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm New Password</Label>
                <Input type={show ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="confirm-password-input" className="rounded-sm" />
              </div>
              {error && (
                <div className="flex items-center gap-2 text-red-500 text-sm bg-red-500/10 border border-red-500/30 p-3 rounded-sm" data-testid="settings-error">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" /> <span>{error}</span>
                </div>
              )}
              <Button type="submit" disabled={loading} data-testid="save-password-btn" className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider">
                {loading ? 'Saving…' : 'Update Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </EmployeeLayout>
  );
}
